/**
 * Template Grid Component
 * Displays a responsive grid of email template cards
 *
 * @file src/components/email/templates/TemplateGrid.tsx
 * @created 2025-10-22
 */

import React from 'react';
import { EmailTemplate } from '@/api/email';
import TemplateCard from './TemplateCard';

interface TemplateGridProps {
  templates: EmailTemplate[];
  onTemplateClick: (template: EmailTemplate) => void;
  onTemplateDelete?: (template: EmailTemplate) => void;
  onTemplateDuplicate?: (template: EmailTemplate) => void;
  onTemplatePreview?: (template: EmailTemplate) => void;
  onCapturePreview?: (template: EmailTemplate) => void;
  showCustomizeButton?: boolean;
  actionLabel?: string;
  emptyMessage?: string;
  userRole?: string;
}

/**
 * TemplateGrid Component
 *
 * Displays templates in a responsive grid layout:
 * - 3 columns on desktop
 * - 2 columns on tablet
 * - 1 column on mobile
 *
 * @example
 * <TemplateGrid
 *   templates={templates}
 *   onTemplateClick={(template) => handleClick(template)}
 *   showCustomizeButton={true}
 *   emptyMessage="No templates found"
 * />
 */
const TemplateGrid: React.FC<TemplateGridProps> = ({
  templates,
  onTemplateClick,
  onTemplateDelete,
  onTemplateDuplicate,
  onTemplatePreview,
  onCapturePreview,
  showCustomizeButton = false,
  actionLabel,
  emptyMessage = 'No templates available',
  userRole
}) => {
  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-gray-400 mb-4">
          <svg
            className="h-16 w-16 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <p className="text-gray-600 font-medium">{emptyMessage}</p>
        <p className="text-sm text-gray-500 mt-2">
          {showCustomizeButton
            ? 'System templates will appear here when available'
            : 'Templates you save will appear here'
          }
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {templates.map((template) => (
        <TemplateCard
          key={template.id}
          template={template}
          onSelect={onTemplateClick}
          onDelete={onTemplateDelete}
          onDuplicate={onTemplateDuplicate}
          onPreview={onTemplatePreview}
          onCapturePreview={onCapturePreview}
          showCustomizeButton={showCustomizeButton}
          actionLabel={actionLabel}
          userRole={userRole}
        />
      ))}
    </div>
  );
};

export default TemplateGrid;
